import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BarChart3,
  BookOpenCheck,
  ChevronRight,
  CreditCard,
  FileCheck2,
  LogOut,
  UserRound,
} from "lucide-react";

const navItems = [
  {
    label: "Dashboard",
    href: "/dashboard",
    icon: BarChart3,
    match: (pathname) => pathname === "/dashboard",
  },
  {
    label: "Practice Material",
    href: "/practice",
    icon: BookOpenCheck,
    match: (pathname) => pathname.startsWith("/practice"),
  },
  {
    label: "Mock Test",
    href: "/mockpage",
    icon: FileCheck2,
    match: (pathname) =>
      pathname.startsWith("/mockpage") || pathname.startsWith("/mocktest"),
  },
  {
    label: "Billing",
    href: "/billing",
    icon: CreditCard,
    match: (pathname) => pathname.startsWith("/billing"),
  },
  {
    label: "Account",
    href: "/account",
    icon: UserRound,
    match: (pathname) => pathname.startsWith("/account"),
  },
];

export const SidebarNav = ({ onNavigate, compact = false }) => {
  const location = useLocation();

  return (
    <nav aria-label="Dashboard navigation">
      <ul className="space-y-1.5">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = item.match(location.pathname);

          return (
            <li key={item.href}>
              <a
                href={item.href}
                onClick={onNavigate}
                aria-current={isActive ? "page" : undefined}
                className={`group flex min-h-11 items-center gap-3 rounded-md px-3 py-2.5 text-sm font-semibold transition ${
                  isActive
                    ? "bg-purple-50 text-purple-800 ring-1 ring-purple-100"
                    : "text-gray-500 hover:bg-slate-50 hover:text-gray-800"
                }`}
              >
                <span
                  className={`grid h-9 w-9 shrink-0 place-items-center rounded-md transition ${
                    isActive
                      ? "bg-white text-purple-700 shadow-sm"
                      : "bg-slate-100 text-gray-500 group-hover:bg-white group-hover:text-purple-700 group-hover:shadow-sm"
                  }`}
                >
                  <Icon className="h-4.5 w-4.5" aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1 truncate">{item.label}</span>
                {!compact && (
                  <ChevronRight
                    className={`h-4 w-4 shrink-0 transition ${
                      isActive
                        ? "text-purple-500"
                        : "text-gray-300 group-hover:translate-x-0.5 group-hover:text-gray-500"
                    }`}
                    aria-hidden="true"
                  />
                )}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export const SidebarLogoutButton = ({ onLogout }) => (
  <button
    onClick={onLogout}
    type="button"
    className="group flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-semibold text-gray-500 transition hover:bg-red-50 hover:text-red-700"
  >
    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-slate-100 text-gray-500 transition group-hover:bg-white group-hover:text-red-600 group-hover:shadow-sm">
      <LogOut className="h-4.5 w-4.5" aria-hidden="true" />
    </span>
    <span className="flex-1 text-left">Logout</span>
  </button>
);

export default function Sidebar() {
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      const response = await fetch("/logout", {
        method: "GET",
        credentials: "include",
      });

      if (response.status === 200) {
        navigate("/login");
        console.log("Logout Successfully");
      } else {
        console.error("Logout Failed");
      }
    } catch (error) {
      console.error("Error During Logout:", error);
    }
  };

  return (
    <aside className="fixed inset-y-0 left-0 z-10 hidden w-72 border-r border-slate-200 bg-white sm:flex sm:flex-col">
      <div className="flex min-h-0 flex-1 flex-col px-4 pb-4 pt-24">
        <a
          className="mb-7 block rounded-md px-3 py-3 text-gray-900 transition hover:bg-slate-50"
          href="/dashboard"
        >
          <img
            className="h-10 w-auto max-w-[165px] object-contain"
            src="/image/prep2.png"
            alt="PrepNexis"
          />
          <span className="mt-2 inline-flex rounded-md bg-purple-50 px-2.5 py-1 text-xs font-semibold uppercase text-purple-700 ring-1 ring-purple-100">
            PTE preparation
          </span>
        </a>

        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          <SidebarNav />
        </div>
      </div>

      <div className="border-t border-slate-100 bg-white p-3">
        <SidebarLogoutButton onLogout={handleLogout} />
      </div>
    </aside>
  );
}
